import { useEffect, useRef, type ReactNode, type CSSProperties, type ElementType } from 'react'
import { cn } from '../../lib/cn'

/** Scroll reveal: toggles .lp-in once the element enters the viewport. */
export function Reveal({ children, className, delay = 0, y, as: Tag = 'div', style }: {
  children: ReactNode
  className?: string
  delay?: number
  y?: number
  as?: ElementType
  style?: CSSProperties
}) {
  const ref = useRef<HTMLElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (!('IntersectionObserver' in window)) { el.classList.add('lp-in'); return }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { el.classList.add('lp-in'); io.disconnect() }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 })
    io.observe(el)
    return () => io.disconnect()
  }, [])
  const vars = { '--lp-reveal-delay': `${delay}ms`, ...(y !== undefined && { '--lp-reveal-y': `${y}px` }), ...style } as CSSProperties
  return <Tag ref={ref} className={cn('lp-reveal', className)} style={vars}>{children}</Tag>
}

/** Reading progress rail across the very top of a public page. */
export function ScrollRail() {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    let raf = 0
    const update = () => {
      raf = 0
      const doc = document.documentElement
      const max = doc.scrollHeight - doc.clientHeight
      const p = max > 0 ? Math.min(1, window.scrollY / max) : 0
      if (ref.current) ref.current.style.transform = `scaleX(${p})`
    }
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update) }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf) }
  }, [])
  return <div className="lp-rail" aria-hidden><div ref={ref} className="lp-rail-fill" /></div>
}
